// src/lib/safeGet.js
// Leituras PONTUAIS do Firestore (getDocs/getDoc) com retry e desvio do cache
// local. É o equivalente do safeOnSnapshot para leituras que não são listeners.
//
// Por que existe: com cache persistente (IndexedDB), o SDK registra a consulta
// no cache ANTES de ir ao servidor ("Allocate target"). Quando o IndexedDB do
// navegador aborta a transação — Safari/iOS restaurando a aba, armazenamento
// cheio/limpo, várias abas, aba privada — a leitura falha com
// `IndexedDbTransactionError [code=unavailable]` sem nunca ter saído do
// dispositivo. Aconteceu na tela de cadastro: a consulta a `emailcheck` morria
// e o usuário via a mensagem crua do SDK. Na segunda tentativa funcionava.
//
// Estratégia: erro transitório → tenta de novo com backoff; se o problema é o
// cache local, refaz a leitura DIRETO NO SERVIDOR (getDocsFromServer /
// getDocFromServer), que não toca no IndexedDB.
import {
  getDocs,
  getDoc,
  getDocsFromServer,
  getDocFromServer,
} from "firebase/firestore";

// Mesmos códigos transitórios do safeOnSnapshot: rede/sessão momentaneamente
// fora, ou token ainda não pronto no boot.
const RETRIABLE = new Set(["unavailable", "unauthenticated", "aborted", "internal"]);
const DEFAULT_MAX_RETRIES = 2;

// True quando a falha veio do armazenamento local do navegador, e não do
// servidor — nesse caso insistir pelo caminho normal repete o mesmo erro.
export const isLocalCacheError = (error) => {
  const name = String(error?.name || "");
  const msg = String(error?.message || "");
  return (
    name === "IndexedDbTransactionError" ||
    /IndexedDb|IndexedDB|AbortError|Allocate target|QuotaExceeded/i.test(msg)
  );
};

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const withRetry = async (readCached, readFromServer, label, maxRetries) => {
  let lastError = null;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      // Falha de cache já detectada: nem tenta o caminho normal de novo.
      const useServer = attempt > 0 && isLocalCacheError(lastError);
      return useServer ? await readFromServer() : await readCached();
    } catch (error) {
      lastError = error;
      const retriable = RETRIABLE.has(error?.code) || isLocalCacheError(error);
      if (!retriable || attempt === maxRetries) throw error;
      console.warn(
        `[safeGet:${label}] ${error?.code || error?.name || "erro"} — tentativa ${attempt + 1}:`,
        error?.message
      );
      await sleep(400 * 2 ** attempt); // 400ms, 800ms…
    }
  }
  throw lastError;
};

/**
 * Drop-in para `getDocs(query)`.
 * @param {*} reference Query | CollectionReference
 * @param {string} [label] identificação nos logs
 */
export const safeGetDocs = (reference, label = "query") =>
  withRetry(
    () => getDocs(reference),
    () => getDocsFromServer(reference),
    label,
    DEFAULT_MAX_RETRIES
  );

/**
 * Drop-in para `getDoc(ref)`.
 * @param {*} reference DocumentReference
 * @param {string} [label] identificação nos logs
 */
export const safeGetDoc = (reference, label = "doc") =>
  withRetry(
    () => getDoc(reference),
    () => getDocFromServer(reference),
    label,
    DEFAULT_MAX_RETRIES
  );
